import { BROWSER_EVENTS, INPUT_VARIANTS } from '@cms/utils/constants'
import Dialog, { DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { Button } from './Button'
import { CancelButton } from './shared'

interface SystemDialogProps {
    content: {
        component: React.ReactNode
    };

    isDialogOpen: boolean;
    handleCloseDialog: () => void;
    handleSubmit: () => void;

    dialogProps?: Pick<DialogProps, "maxWidth" | "disableScrollLock" | "disableRestoreFocus"> & {
        title?: string;
        contentStyle?: React.CSSProperties;
    };
    actionsProps?: {
        hide?: boolean;

        submit?: React.ComponentProps<typeof Button> & {
            label?: string;
            onClick?: () => void;
            component?: React.ReactNode;
        };

        cancel?: React.ComponentProps<typeof Button> & {
            label?: string;
            onClick?: () => void;
        }
    }
}

interface SystemDialogContentProps extends
    Pick<SystemDialogProps,
        'content' |
        'actionsProps' |
        'dialogProps'
    > { }


export const SystemDialogContent: React.FC<SystemDialogContentProps> = (props) => {
    const {
        content,
        dialogProps,
        actionsProps,
    } = props;

    const handleCancel = () => {
        if (actionsProps?.cancel?.onClick) {
            actionsProps.cancel.onClick()
        }
        else {
            dispatchEvent(new Event(BROWSER_EVENTS.CLOSE_MODAL));
        }
    }

    return (
        <>
            <DialogTitle
                className={INPUT_VARIANTS['white_text_dark_bg']}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <span className='text-lg'>
                    {dialogProps?.title}
                </span>

                <CancelButton
                    onClick={handleCancel}
                />
            </DialogTitle>

            <DialogContent
                style={{
                    padding: '16px',
                    ...dialogProps?.contentStyle,
                }}
            >
                {content.component}
            </DialogContent>

            <DialogActions
                hidden={actionsProps?.hide}
            >
                <Button
                    {...actionsProps?.cancel}
                    onClick={handleCancel}
                >
                    {actionsProps?.cancel?.label || 'Cancel'}
                </Button>
                {actionsProps?.submit?.component ? (
                    actionsProps?.submit?.component
                ) : (
                    <Button
                        inputVariant='white_text_dark_bg'
                        {...actionsProps?.submit}
                    >
                        {actionsProps?.submit?.label || 'Submit'}
                    </Button>
                )}
            </DialogActions>
        </>
    )
}

interface ControlledSystemDialogProps extends
    Omit<SystemDialogProps,
        "isDialogOpen" |
        "handleCloseDialog" |
        "handleSubmit"
    > {
    trigger: {
        component: React.ReactNode;
        wrapperClassName?: string;
        disabled?: boolean;
        onIsDialogOpenChange?: (isOpen: boolean) => void;
    };
}

export const ControlledSystemDialog: React.FC<ControlledSystemDialogProps> = (props) => {
    const {
        trigger,
        content,
        actionsProps,
        dialogProps
    } = props

    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const handleCloseDialog = () => {
        setIsDialogOpen(false)
    }

    const handleOpenDialog = () => {
        if (trigger?.disabled) return;
        setIsDialogOpen(true)
    }

    const handleSubmit = () => {
        if (actionsProps?.submit?.disabled) return;

        if (actionsProps?.submit?.onClick) {
            actionsProps.submit.onClick()
        }

        handleCloseDialog()
    }

    useEffect(() => {
        const handler = () => {
            handleCloseDialog();
        }

        window.addEventListener(BROWSER_EVENTS.CLOSE_MODAL, handler);

        return () => window.removeEventListener(BROWSER_EVENTS.CLOSE_MODAL, handler);
    }, []);

    useEffect(() => {
        if (trigger?.onIsDialogOpenChange) {
            trigger.onIsDialogOpenChange(isDialogOpen)
        }
    }, [isDialogOpen, trigger])

    return (
        <React.Fragment>
            <div
                onClickCapture={handleOpenDialog}
                className={clsx(
                    trigger.wrapperClassName
                )}
            >
                {trigger.component}
            </div>

            <Dialog
                fullWidth
                maxWidth={dialogProps?.maxWidth || false}
                disableScrollLock={dialogProps?.disableScrollLock ?? true}
                disableRestoreFocus={dialogProps?.disableRestoreFocus ?? true}

                open={isDialogOpen}
                onClose={handleCloseDialog}
            >
                <SystemDialogContent
                    content={content}
                    dialogProps={dialogProps}
                    actionsProps={{
                        ...actionsProps,
                        submit: {
                            ...actionsProps?.submit,
                            onClick: handleSubmit
                        },
                        cancel: {
                            ...actionsProps?.cancel,
                            onClick: handleCloseDialog
                        }
                    }}
                />
            </Dialog>
        </React.Fragment>
    )
}